import * as Label from "@radix-ui/react-label";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Label + control + help/error, wired together for screen readers.
 *
 * The error replaces the hint rather than stacking under it: two lines of small text under a
 * field, one of which contradicts the other, is how forms get misread. `aria-describedby`
 * points at whichever is showing, and `role="alert"` announces the error when it appears.
 *
 * The label is `${htmlFor}-label`, so a control with more than one button can name the others
 * after it: the audio picker's "Clear Hold music".
 */
export function Field({
  label, htmlFor, hint, error, required, className, children, action,
}: {
  label: string;
  htmlFor: string;
  hint?: React.ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
  /** Optional control on the right of the label row — "Generate", "Reveal", and so on. */
  action?: React.ReactNode;
}) {
  const describedBy = error ? `${htmlFor}-error` : hint ? `${htmlFor}-hint` : undefined;
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between gap-2">
        <Label.Root
          id={`${htmlFor}-label`}
          htmlFor={htmlFor}
          className="text-xs font-medium text-text flex items-center gap-1"
        >
          {label}
          {required && (
            <span className="text-danger-9" aria-hidden>
              *
            </span>
          )}
        </Label.Root>
        {action}
      </div>
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
            id: htmlFor,
            "aria-describedby": describedBy,
          })
        : children}
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs text-danger-11">
          {error}
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="text-xs text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
